/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CHECKOUT_DISCOUNT_KEY = 'mm_checkout_discount';

const CHECKOUT_DISCOUNT_CLOSE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M12.8863 12.8836L5.11914 5.11642" stroke="currentColor" stroke-width="1.5" stroke-linejoin="bevel"/><path d="M5.11758 12.8836L12.8848 5.11642" stroke="currentColor" stroke-width="1.5" stroke-linejoin="bevel"/></svg>`;

const CART_CONFIG = {
	// API Settings
	debounceDelay: 200,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

	// Development mode (lazy evaluation)
	get isDevelopment() {
		return typeof isDevelopment !== 'undefined' ? isDevelopment : false;
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Logging system
const cartLog = (level, message, data = {}) => {
	if (!CART_CONFIG.isDevelopment && level === 'info') return;
	if (typeof console !== 'undefined') {
		const logFn = console[level] || console.log;
		logFn(`🛒 [CART] ${message}`, { timestamp: window.getTimestamp?.() || new Date().toLocaleString(), ...data });
	}
};

// Debounce function
// Use global debounce from mm_utils.js
const cartDebounce = window.debounce || ((fn, delay) => {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => fn.apply(this, args), delay);
	};
});

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count only when item_count is explicitly present (e.g. cart.js / cart/change.js)
		// cart/add.js returns a line item, not the cart — no item_count, so skip to avoid hiding the bubble
		if (data.item_count !== undefined) {
			const count = data.item_count;
			DOM.cartBubbles.forEach(bubble => {
				bubble.textContent = count < 10 ? '0' + count : count;
				bubble.classList.toggle('is-visible', count > 0);
			});
			cartEmit('count-updated', { count });
		}

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			// Capture avant swap :
			// - IDs items existants → détecter les nouveaux produits
			// - présence d'un palier atteint → flag pour animer le check pop
			const prevItemIds = new Set(
				Array.from(currentCart.querySelectorAll('[data-cart-item-id]'))
					.map((el) => el.dataset.cartItemId)
			);
			const prevWasComplete = !!currentCart.querySelector('.mm-progress-bar--complete');

			currentCart.innerHTML = newCart.innerHTML;

			// Marque les items nouvellement apparus (on animera `.is-entering`
			// soit tout de suite si le drawer est ouvert, soit à l'ouverture).
			currentCart.querySelectorAll('[data-cart-item-id]').forEach((el) => {
				if (!prevItemIds.has(el.dataset.cartItemId)) {
					el.dataset.isNew = 'true';
				}
			});

			// Flag pour déclencher le pop du check icon quand on vient de
			// franchir un palier (consommé par playCartIntroAnimation).
			const nowComplete = !!currentCart.querySelector('.mm-progress-bar--complete');
			if (nowComplete && !prevWasComplete) _checkPopPending = true;
			// Reinitialize Splide sliders with auto-init
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element._splide) {
						try {
							new Splide(element).mount();
						} catch (error) {
							cartLog('warn', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Cross-sell recos fired via cart:refreshed event after DOM is stable
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			if (typeof initDetailsAnimation === 'function') {
				initDetailsAnimation();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape' || e.key === 'Esc') {
				e.preventDefault();
				this.close();
				this.focusTrap.triggerElement?.focus();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		cartLog('info', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			cartLog('info', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');
			wrapper.setAttribute('aria-hidden', 'false');
			html.classList.add('stop-scrolling');
			window.LenisManager?.stop();

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Setup focus trap
			this.setupFocusTrap(wrapper, triggerElement);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper) {
			wrapper.classList.remove('mm-dc-is-open');
			wrapper.setAttribute('aria-hidden', 'true');
		}
		if (html) {
			html.classList.remove('stop-scrolling');
			window.LenisManager?.resume();
		}

		// Remove focus trap
		this.removeFocusTrap();

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;
		
		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;
		
		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	async updateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;
		
		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });
		
		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
			
			// Shopify cart/change.js requires 1-based line index and proper sections format
			const requestBody = {
				line: parseInt(lineIndex, 10), // Should be 1-based from template
				quantity: newQty
			};
			
			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}
			
			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);
			
			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => cartLog('error', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!data.sections?.[sectionId]) return data;

		const temp = document.createElement('div');
		temp.innerHTML = data.sections[sectionId];
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);
		
		if (!freeProductsEl?.children.length) return data;

		// Aggregate decisions per productId: if ANY milestone unlocks a product, keep it
		const productDecisions = {};
		Array.from(freeProductsEl.children).forEach(el => {
			const variantId = el.dataset.variantId;
			const productId = el.dataset.productId;
			const shouldHave = el.dataset.freeProduct === 'true';

			if (!productDecisions[productId]) {
				productDecisions[productId] = { variantId, shouldHave: false };
			}
			if (shouldHave) {
				productDecisions[productId].shouldHave = true;
			}
		});

		const updates = {};
		Object.entries(productDecisions).forEach(([productId, { variantId, shouldHave }]) => {
			const cartItem = document.querySelector(`.mm-dc-free-product-wrapper[data-product-id="${productId}"]`);
			const hasIt = !!cartItem;

			if (hasIt && !shouldHave) {
				const actualVariantId = cartItem.dataset.cartItemId || variantId;
				updates[actualVariantId] = 0;
				cartEmit('free-product-removing', { variantId: actualVariantId });
			} else if (!hasIt && shouldHave) {
				updates[variantId] = 1;
				cartEmit('free-product-adding', { variantId });
			}
		});

		if (Object.keys(updates).length > 0) {
			try {
				// Shopify cart/update.js expects updates object and optional sections
				const requestBody = { updates };
				
				if (sectionId) {
					requestBody.sections = sectionId;
				}
				
				await CartAPI.request('POST', 'cart/update.js', requestBody);
				cartEmit('free-products-updated', { updates });
			} catch (error) {
				cartEmit('free-products-error', { error: error.message });
			}
		}

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = cartDebounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		debouncedUpdate(lineIndex);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	debouncedUpdate(lineIndex);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Adds loading state to checkout button
window.mmLoadThis = (element) => {
	if (element) {
		element.classList.add('mm-loading');
		cartEmit('checkout-loading', { element });
	}
};

/**
 * Anime le(s) fill(s) de la progress bar depuis 0% vers leur valeur cible
 * inline. Appelé à chaque render du panier (load + après refreshDOM) pour
 * que la transition `width` se déclenche bien — sinon l'élément naît déjà
 * à sa width finale et la transition CSS ne joue pas.
 */
const animateProgressBarFills = (root = document) => {
	const fills = root.querySelectorAll('.mm-progress-bar__fill');
	fills.forEach((fill) => {
		const target = fill.style.width;
		if (!target || target === '0%' || target === '0') return;
		fill.style.width = '0%';
		void fill.offsetWidth;
		requestAnimationFrame(() => {
			fill.style.width = target;
		});
	});
};

/**
 * Flag posé par refreshDOM quand on passe en --complete (palier atteint).
 * Consommé par playCartIntroAnimation pour déclencher le pop du check.
 */
let _checkPopPending = false;

/**
 * Joue les animations d'update panier :
 * 1. Les nouveaux items s'affichent (fade + slide + expand max-height)
 * 2. Après un petit décalage, le fill de la progress bar glisse vers sa
 *    nouvelle valeur (transition CSS déjà en place)
 * 3. Si un palier a été franchi, le check icon pop
 *
 * Appelée juste après refreshDOM si le drawer est ouvert, ou différée à
 * cart:opened si le drawer était fermé (pour que l'anim soit visible).
 */
const playCartIntroAnimation = () => {
	// --- Étape 1 : entrée du nouveau produit ---
	// Tant que l'item portait `data-is-new`, il était collapse/invisible
	// (voir CSS). On retire le marqueur et on pose `.is-entering` pour
	// déclencher l'animation d'entrée (fade + expand max-height).
	const newItems = Array.from(document.querySelectorAll('[data-is-new="true"]'));
	newItems.forEach((el) => {
		delete el.dataset.isNew;
		el.classList.add('is-entering');
		el.addEventListener(
			'animationend',
			() => el.classList.remove('is-entering'),
			{ once: true }
		);
	});

	// Décalage pour que les deux phases s'enchaînent au lieu de se
	// superposer : zone vide → produit qui s'insère → barre qui bouge.
	const progressDelay = newItems.length > 0 ? 440 : 0;

	setTimeout(() => {
		// --- Étape 2 : fill de la progress bar ---
		// Reset à 0% puis retour à la valeur cible → la transition CSS
		// (width 700ms ease-out-slow) se déclenche.
		animateProgressBarFills();

		// --- Étape 3 : pop du check si palier atteint ---
		if (_checkPopPending) {
			_checkPopPending = false;
			document.querySelectorAll('.mm-progress-bar--complete').forEach((bar) => {
				bar.classList.add('is-check-popping');
			});
		}
	}, progressDelay);
};

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Checkout button loading
		if (e.target.closest('[data-checkout-btn]')) {
			window.mmLoadThis(e.target.closest('[data-checkout-btn]'));
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});

	// Animation de suppression d'un item : on a 500ms de debounce avant que
	// Shopify ne re-render la section, largement de quoi jouer le fade-out
	// (300ms) côté CSS. Tous les chemins de remove (bouton corbeille + qty
	// décrémentée à 0) émettent `cart:item-removed`.
	document.addEventListener('cart:item-removed', (e) => {
		const lineIndex = e.detail?.data?.lineIndex;
		if (lineIndex == null) return;
		const item = DOM.cartItem(lineIndex);
		if (item) item.classList.add('is-removing');
	});

	// Après un refreshDOM, on veut que :
	// 1. le fill de la progress bar reparte de 0
	// 2. les nouveaux items apparaissent (fade + slide + collapse inversé)
	// 3. si un palier est franchi, le check icon "valide" avec un rebond
	// Si le drawer est ouvert : on joue tout de suite. Si le user a ajouté
	// depuis une page produit sans ouvrir le drawer avant, on diffère
	// jusqu'à cart:opened pour que l'anim soit visible.
	document.addEventListener('cart:refreshed', () => {
		const drawerOpen = DOM.cartWrapper?.classList.contains('mm-dc-is-open');
		if (drawerOpen) {
			playCartIntroAnimation();
		}
		// Sinon : `data-is-new` et `_checkPopPending` sont déjà posés par
		// refreshDOM, cart:opened les consommera.
	});

	// Quand le drawer s'ouvre, si un refresh récent a laissé des animations
	// à jouer, on les déclenche après le slide-in pour qu'elles soient visibles.
	document.addEventListener('cart:opened', () => {
		const hasPendingItems = !!document.querySelector('[data-is-new="true"]');
		if (hasPendingItems || _checkPopPending) {
			// Le slide-in du drawer dure --duration-modal (650ms). On attend
			// qu'il soit en place avant de jouer les animations internes.
			setTimeout(() => playCartIntroAnimation(), 680);
		}
		// Charge les recommandations si pas encore fait (ex: ouverture directe sans add-to-cart)
		if (typeof triggerRecoLoad === 'function') triggerRecoLoad();
	});

	// Lance les recos après chaque DOM swap (le grid est stable ici)
	document.addEventListener('cart:refreshed', () => {
		if (typeof triggerRecoLoad === 'function') triggerRecoLoad();
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();
			
			const submitButton = form.querySelector('[type="submit"]');
			const formData = new FormData(form);
			
			if (!formData.get('id')) {
				cartLog('error', 'No variant ID found in form', { form });
				return;
			}
			
			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}
			
			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};
			
			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;
				
				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}
				
				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}
			
			cartEmit('add-to-cart-start', extractedData);
			
			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
				
				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}
				
				const response = await CartAPI.request('POST', 'cart/add.js', formData);

				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				// Process free products after add-to-cart
				await Cart.processFreeProducts(response);

				// Close any open modal (e.g. size selector) before opening cart
				if (typeof ModalManager !== 'undefined') ModalManager.close();

				// Auto-open cart after successful add
				Cart.open();
				
			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				cartLog('error', 'Add to cart failed', { 
					error: error.message, 
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Guard attribute to prevent duplicate listeners across refreshDOM calls
	const ATTACHED = 'data-cart-qty-listener-attached';

	// Cart decrease buttons
	document.querySelectorAll(`[data-cart-decrease]:not([${ATTACHED}])`).forEach((btn) => {
		btn.setAttribute(ATTACHED, 'true');
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll(`[data-cart-increase]:not([${ATTACHED}])`).forEach((btn) => {
		btn.setAttribute(ATTACHED, 'true');
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.mmLoadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll(`[data-cart-remove]:not([${ATTACHED}])`).forEach((btn) => {
		btn.setAttribute(ATTACHED, 'true');
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.mmLoadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.mmLoadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 🛍️ CROSS-SELL SIZE SELECTOR
 */
let crossSellSizeSelectorsInitialized = false;

/**
 * Remonte la chaîne des ancêtres pour trouver le premier qui CLIP verticalement
 * (overflow-y auto/scroll/hidden). Permet, dans le drawer du panier, de détecter
 * le vrai bas utile au lieu de partir du viewport (qui englobe le footer sticky).
 */
function getClippingAncestor(el) {
	let node = el.parentElement;
	while (node && node !== document.body) {
		const style = window.getComputedStyle(node);
		const oy = style.overflowY;
		if (oy === 'auto' || oy === 'scroll' || oy === 'hidden') {
			return node;
		}
		node = node.parentElement;
	}
	return null;
}

/**
 * Positionne le dropdown des tailles vers le haut ou le bas selon l'espace
 * disponible — en priorisant les bornes du conteneur scrollable parent (drawer
 * panier) puis en retombant sur le viewport. À appeler après `is-open`.
 */
function positionCrossSizeList(wrapper) {
	if (!wrapper) return;
	const list = wrapper.querySelector('[data-cross-size-list]');
	const trigger = wrapper.querySelector('[data-cross-size-trigger]');
	if (!list || !trigger) return;

	wrapper.classList.remove('is-open-up');

	const triggerRect = trigger.getBoundingClientRect();
	const listHeight = list.offsetHeight;
	const margin = 12;

	// Bornes haute/basse : on prend l'intersection du viewport ET du premier
	// ancêtre scrollable (ex. .mm-dc-products-container dans le drawer).
	let topBound = 0;
	let bottomBound = window.innerHeight;
	const clipper = getClippingAncestor(wrapper);
	if (clipper) {
		const clipRect = clipper.getBoundingClientRect();
		topBound = Math.max(topBound, clipRect.top);
		bottomBound = Math.min(bottomBound, clipRect.bottom);
	}

	const spaceBelow = bottomBound - triggerRect.bottom;
	const spaceAbove = triggerRect.top - topBound;

	// Retourne si pas la place en bas ET qu'il y a (plus de) place au-dessus.
	if (spaceBelow < listHeight + margin && spaceAbove > spaceBelow) {
		wrapper.classList.add('is-open-up');
	}
}

/**
 * Ferme proprement un wrapper de sélecteur de taille en jouant l'animation de
 * sortie (`.is-closing`) avant de retirer complètement les classes. Fallback
 * timeout au cas où `animationend` ne se déclenche pas (display:none, tab
 * inactif, etc.).
 */
function closeCrossSizeWrapper(wrapper) {
	if (!wrapper) return;
	const trigger = wrapper.querySelector('[data-cross-size-trigger]');
	if (trigger) trigger.setAttribute('aria-expanded', 'false');

	// Déjà fermé ou déjà en train de se fermer : ne pas relancer d'anim.
	if (!wrapper.classList.contains('is-open')) {
		if (!wrapper.classList.contains('is-closing')) {
			wrapper.classList.remove('is-open-up');
		}
		return;
	}

	const list = wrapper.querySelector('[data-cross-size-list]');
	const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	wrapper.classList.remove('is-open');
	wrapper.classList.add('is-closing');

	let done = false;
	const cleanup = () => {
		if (done) return;
		done = true;
		wrapper.classList.remove('is-closing');
		wrapper.classList.remove('is-open-up');
		if (list) list.removeEventListener('animationend', onEnd);
		clearTimeout(fallback);
	};
	const onEnd = (e) => {
		if (e.target !== list) return;
		cleanup();
	};
	if (list && !prefersReduced) {
		list.addEventListener('animationend', onEnd);
	}
	const fallback = setTimeout(cleanup, prefersReduced ? 0 : 260);
}

function initCrossSellSizeSelectors() {
	if (crossSellSizeSelectorsInitialized) return;
	crossSellSizeSelectorsInitialized = true;

	document.addEventListener('click', (e) => {
		const wrapper = e.target.closest('[data-cross-size-wrapper]');

		// Close all other open cross-sell dropdowns
		document.querySelectorAll('[data-cross-size-wrapper].is-open').forEach((w) => {
			if (w !== wrapper) closeCrossSizeWrapper(w);
		});

		if (!wrapper) return;
		// Skip free products — handled by initFreeSizeSelectors
		if (wrapper.hasAttribute('data-free-size-wrapper')) return;

		// Toggle trigger
		if (e.target.closest('[data-cross-size-trigger]')) {
			if (wrapper.classList.contains('is-open')) {
				closeCrossSizeWrapper(wrapper);
			} else {
				wrapper.classList.remove('is-closing');
				wrapper.classList.add('is-open');
				wrapper.querySelector('[data-cross-size-trigger]')?.setAttribute('aria-expanded', 'true');
				positionCrossSizeList(wrapper);
			}
			return;
		}

		// Option click
		const option = e.target.closest('.mm-dc-cross-size-option');
		if (!option || option.dataset.available === 'false') return;

		wrapper.querySelectorAll('.mm-dc-cross-size-option').forEach((o) => o.classList.remove('is-selected'));
		option.classList.add('is-selected');

		wrapper.querySelector('[data-cross-size-label]').textContent = option.dataset.value;

		const isAvailable = option.dataset.available !== 'false';
		const form = wrapper.closest('form');
		if (form) {
			const idInput = form.querySelector('input[name="id"]');
			if (idInput) idInput.value = option.dataset.variantId;

			const submitBtn = form.querySelector('[type="submit"]');
			if (submitBtn) {
				submitBtn.disabled = !isAvailable;
				submitBtn.setAttribute('aria-disabled', String(!isAvailable));
			}
		}

		closeCrossSizeWrapper(wrapper);
	});
}

/*********************************************************************************************
 * 🎁 FREE PRODUCT SIZE SELECTOR
 */
let freeSizeSelectorsInitialized = false;

function initFreeSizeSelectors() {
	if (freeSizeSelectorsInitialized) return;
	freeSizeSelectorsInitialized = true;

	document.addEventListener('click', async (e) => {
		const wrapper = e.target.closest('[data-free-size-wrapper]');
		if (!wrapper) return;

		// Toggle trigger (open/close dropdown)
		if (e.target.closest('[data-cross-size-trigger]')) {
			if (wrapper.classList.contains('is-open')) {
				closeCrossSizeWrapper(wrapper);
			} else {
				wrapper.classList.remove('is-closing');
				wrapper.classList.add('is-open');
				wrapper.querySelector('[data-cross-size-trigger]')?.setAttribute('aria-expanded', 'true');
				positionCrossSizeList(wrapper);
			}
			return;
		}

		const option = e.target.closest('.mm-dc-cross-size-option');
		if (!option) return;

		if (option.dataset.available === 'false') return;

		const newVariantId = option.dataset.variantId;
		const lineIndex = parseInt(wrapper.dataset.lineIndex, 10);

		// Update UI immediately
		wrapper.querySelectorAll('.mm-dc-cross-size-option').forEach(o => o.classList.remove('is-selected'));
		option.classList.add('is-selected');
		const label = wrapper.querySelector('[data-cross-size-label]');
		if (label) label.textContent = option.dataset.value;

		closeCrossSizeWrapper(wrapper);

		await swapFreeProductVariant(lineIndex, newVariantId, wrapper);
	});
}

async function swapFreeProductVariant(lineIndex, newVariantId, wrapper) {
	const trigger = wrapper.querySelector('[data-cross-size-trigger]');
	if (trigger) trigger.disabled = true;

	try {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove current line item
		await CartAPI.request('POST', 'cart/change.js', {
			line: lineIndex,
			quantity: 0
		});

		// Add new variant with _size_selected property to distinguish from auto-added
		const addBody = { id: parseInt(newVariantId, 10), quantity: 1, properties: { _size_selected: 'true' } };
		if (sectionId) addBody.sections = sectionId;

		await CartAPI.request('POST', 'cart/add.js', addBody);
		cartEmit('free-product-size-changed', { lineIndex, newVariantId });

	} catch (error) {
		cartLog('error', 'Failed to swap free product variant', { error: error.message });
		if (trigger) trigger.disabled = false;
	}
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */
function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	cartLog('info', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput) {
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			cartLog('info', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				cartLog('info', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		cartLog('info', 'Promo code listeners attached successfully');
	}

	// Restore checkout-only discount after DOM refresh
	restoreCheckoutDiscount();
}

async function applyDiscount() {
	cartLog('info', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		cartLog('error', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	cartLog('info', 'Discount code entered', { code });

	if (!code) {
		cartLog('warn', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		cartLog('info', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		const discountsBefore = cartBefore.cart_level_discount_applications || [];
		cartLog('info', 'Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied at cart level or as checkout-only
		const codeAlreadyApplied = discountsBefore.some(d => {
			const discountCode = d.title || d.code;
			return discountCode && discountCode.toLowerCase() === code.toLowerCase();
		});
		const codeAlreadyCheckout = getCheckoutDiscounts().some(c => c.toLowerCase() === code.toLowerCase());

		if (codeAlreadyApplied || codeAlreadyCheckout) {
			showPromoError('Ce code est déjà appliqué');
			cartLog('info', 'This code is already applied', { code });
			input.value = '';
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			const errorMsg = input.dataset.errorEmptyCart || 'Votre panier est vide';
			showPromoError(errorMsg);
			cartLog('info', 'Cart is empty');
			input.value = '';
			return;
		}

		// Save existing cart-level discount — applying a new code via cart API
		// will replace it; we may need to restore it if the new code is checkout-only.
		const cartLevelCodeToRestore = discountsBefore.length > 0
			? (discountsBefore[0].title || discountsBefore[0].code)
			: null;

		// Apply discount via cart/update.js API
		cartLog('info', 'Applying discount via API...', { code, cartLevelCodeToRestore });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = { discount: code };
		if (sectionId) updatePayload.sections = sectionId;

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) throw new Error('Cart update failed');

		const updateData = await response.json();
		cartLog('info', 'Cart updated', { updateData });

		// Verify if discount was applied
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const codeApplied = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		// Helper: restore a previous cart-level discount and refresh DOM
		const restoreCartDiscount = async (discountCode) => {
			const payload = { discount: discountCode };
			if (sectionId) payload.sections = sectionId;
			try {
				const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(payload)
				});
				const data = await res.json();
				if (data.sections?.[sectionId]) CartAPI.refreshDOM(data.sections[sectionId]);
				cartLog('info', 'Cart discount restored', { discountCode });
			} catch (e) {
				cartLog('error', 'Failed to restore cart discount', { error: e.message });
			}
		};

		if (!codeApplied) {
			// Check if Shopify accepted the code but it's checkout-only
			// (e.g. free shipping, BXGY — they don't appear in cart_level_discount_applications)
			const discountCodes = cartAfter.discount_codes || [];
			const codeInCart = discountCodes.some(d => {
				const dc = typeof d === 'string' ? d : (d.code || d.title || '');
				return dc.toLowerCase() === code.toLowerCase();
			});

			if (codeInCart) {
				// Code is checkout-only — restore any existing cart-level code, then store this one
				cartLog('info', 'Checkout-only discount code stored', { code, cartLevelCodeToRestore });
				if (cartLevelCodeToRestore) await restoreCartDiscount(cartLevelCodeToRestore);
				input.value = '';
				setCheckoutDiscount(code);
				const checkoutMsg = input.dataset.checkoutDiscountText || 'Ce code sera appliqué au checkout';
				showPromoSuccess(checkoutMsg);
				return;
			}

			// Truly invalid code — restore or cleanup, then show error
			cartLog('warn', 'Discount was not applied by Shopify', { code });
			if (cartLevelCodeToRestore) {
				await restoreCartDiscount(cartLevelCodeToRestore);
			} else {
				const cleanupPayload = { discount: '' };
				if (sectionId) cleanupPayload.sections = sectionId;
				try {
					await fetch(window.Shopify.routes.root + 'cart/update.js', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify(cleanupPayload)
					});
				} catch (cleanupError) {
					cartLog('error', 'Failed to clean invalid code', { error: cleanupError.message });
				}
			}

			const errorMsg = input.dataset.errorNotApplicable || 'Ce code promo ne peut pas être appliqué à votre panier';
			showPromoError(errorMsg);
			input.value = '';
			return;
		}

		// Discount applied successfully, refresh UI
		cartLog('info', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Clear input
		input.value = '';

		// Show success message
		showPromoSuccess(`Code "${code}" appliqué`);

		// Emit success event
		cartEmit('discount-applied', { code });
		cartLog('info', 'Discount applied and cart refreshed');

	} catch (error) {
		cartLog('error', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError('Erreur lors de l\'application du code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		const span = errorElement.querySelector('span');
		if (span) span.textContent = message;
		else errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		const span = errorElement.querySelector('span');
		if (span) span.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

function showPromoSuccess(message) {
	const successElement = document.querySelector('[data-promo-success]');
	if (successElement) {
		const span = successElement.querySelector('span');
		if (span) span.textContent = message;
		successElement.classList.remove('mm-none');
	}

	setTimeout(() => {
		const el = document.querySelector('[data-promo-success]');
		if (el) {
			el.classList.add('mm-none');
			const span = el.querySelector('span');
			if (span) span.textContent = '';
		}
	}, 5000);
}

// ─── Checkout-only discount (codes that only apply at checkout) ───────────────

function getCheckoutDiscounts() {
	try {
		const raw = sessionStorage.getItem(CHECKOUT_DISCOUNT_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		// backward compat: old storage was a plain string
		return Array.isArray(parsed) ? parsed : [parsed];
	} catch {
		return [];
	}
}

function saveCheckoutDiscounts(codes) {
	sessionStorage.setItem(CHECKOUT_DISCOUNT_KEY, JSON.stringify(codes));
}

function getCartLevelDiscountCodes() {
	return Array.from(document.querySelectorAll('[data-discount-code]'))
		.map(el => el.dataset.discountCode)
		.filter(Boolean);
}

function updateCheckoutBtnUrl() {
	const btn = document.querySelector('[data-checkout-btn]');
	if (!btn) return;
	const cartLevelCodes = getCartLevelDiscountCodes();
	const checkoutOnlyCodes = getCheckoutDiscounts();
	// Deduplicate (case-insensitive), cart-level codes first
	const seen = new Set(cartLevelCodes.map(c => c.toLowerCase()));
	const allCodes = [
		...cartLevelCodes,
		...checkoutOnlyCodes.filter(c => !seen.has(c.toLowerCase()))
	];
	const root = window.Shopify.routes.root; // e.g. '/', '/de/', '/fr-ca/'
	btn.href = allCodes.length
	? `${root}checkout?discount=${allCodes.map(encodeURIComponent).join(',')}`
	: `${root}checkout`;
}

function removeAllCheckoutDiscountTags() {
	document.querySelectorAll('[data-checkout-discount-tag]').forEach(el => el.remove());
}

function renderCheckoutDiscountTags() {
	removeAllCheckoutDiscountTags();
	const codes = getCheckoutDiscounts();
	if (!codes.length) return;
	const container = document.querySelector('.mm-dc-details-content');
	if (!container) return;
	codes.forEach(code => {
		const div = document.createElement('div');
		div.className = 'mm-dc-promo-applied mm-flex mm-align-center mm-justify-between';
		div.dataset.checkoutDiscountTag = code.toLowerCase();
		const escapedCode = code.replace(/"/g, '&quot;');
		div.innerHTML = `
			<button type="button"
				class="mm-dc-discount-button mm-cursor-pointer mm-flex mm-align-center mm-text-small"
				data-checkout-discount-remove
				aria-label="Supprimer le code promotionnel ${escapedCode}">
				${escapedCode}
				${CHECKOUT_DISCOUNT_CLOSE_SVG}
			</button>
			<span class="mm-dc-discount-amount mm-text mm-black-500">→ checkout</span>
		`;
		div.querySelector('[data-checkout-discount-remove]').addEventListener('click', () => {
			removeCheckoutDiscount(code);
		});
		container.insertBefore(div, container.firstChild);
	});
}

function setCheckoutDiscount(code) {
	const codes = getCheckoutDiscounts();
	if (!codes.some(c => c.toLowerCase() === code.toLowerCase())) {
		codes.push(code);
		saveCheckoutDiscounts(codes);
	}
	updateCheckoutBtnUrl();
	renderCheckoutDiscountTags();
}

function removeCheckoutDiscount(code) {
	const codes = getCheckoutDiscounts().filter(c => c.toLowerCase() !== code.toLowerCase());
	saveCheckoutDiscounts(codes);
	updateCheckoutBtnUrl();
	renderCheckoutDiscountTags();
}

function restoreCheckoutDiscount() {
	updateCheckoutBtnUrl();
	renderCheckoutDiscountTags();
}

// ─────────────────────────────────────────────────────────────────────────────

window.removeDiscount = async function(code) {
	cartLog('info', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		cartLog('info', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		cartLog('info', 'Discount removal response', { updateData });

		// Verify discount was removed
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = cartAfter.cart_level_discount_applications?.some(d => {
			const discountTitle = d.title || d.code;
			return discountTitle && discountTitle.toLowerCase() === code.toLowerCase();
		});

		if (discountStillPresent) {
			cartLog('error', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		cartLog('info', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		cartLog('info', 'Discount removed and cart refreshed');

	} catch (error) {
		cartLog('error', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 🎞️ DETAILS ANIMATION (promo + preorder dropdowns)
 */
function animateDetails(details, open) {
	const content = details.querySelector('.mm-dc-details-content');
	if (!content || details._mmAnimating) return;
	details._mmAnimating = true;

	if (open) {
		details.setAttribute('open', '');
		content.style.maxHeight = '0px';
		content.style.opacity = '0';
		content.offsetHeight; // force reflow → commit 0 values before animating
		details.classList.add('mm-details-open');
		content.style.maxHeight = content.scrollHeight + 'px';
		content.style.opacity = '1';
		content.addEventListener('transitionend', function handler(e) {
			if (e.propertyName !== 'max-height') return;
			content.style.maxHeight = '';
			content.style.opacity = '';
			details._mmAnimating = false;
			content.removeEventListener('transitionend', handler);
		});
	} else {
		details.classList.remove('mm-details-open');
		details.classList.add('mm-details-closing');
		content.style.maxHeight = content.scrollHeight + 'px'; // set explicit start height
		content.style.opacity = '1';
		content.offsetHeight; // force reflow → commit height before animating to 0
		content.style.maxHeight = '0px';
		content.style.opacity = '0';
		content.addEventListener('transitionend', function handler(e) {
			if (e.propertyName !== 'max-height') return;
			details.removeAttribute('open');
			details.classList.remove('mm-details-closing');
			content.style.maxHeight = '';
			content.style.opacity = '';
			details._mmAnimating = false;
			content.removeEventListener('transitionend', handler);
		});
	}
}

function initDetailsAnimation() {
	document.querySelectorAll('.mm-dc-promo-details, .mm-dc-preorder-details').forEach(details => {
		const summary = details.querySelector('summary');
		if (!summary || summary._mmDetailsAnimInit) return;
		summary._mmDetailsAnimInit = true;

		summary.addEventListener('click', (e) => {
			e.preventDefault();
			animateDetails(details, !details.open);
		});

		// If already open on load (e.g. discount applied), reflect icon state
		if (details.open) {
			details.classList.add('mm-details-open');
		}
	});
}

/*********************************************************************************************
 * 🔍 SMART CROSS-SELL RECOMMENDATIONS
 */
const _recoCacheMap = new Map();
const RECO_CACHE_TTL = 5 * 60 * 1000;

let _recoDebounceTimer = null;
function triggerRecoLoad() {
	clearTimeout(_recoDebounceTimer);
	_recoDebounceTimer = setTimeout(mmLoadCartRecommendations, 350);
}

function getRecoCache(key) {
	const entry = _recoCacheMap.get(key);
	if (!entry) return null;
	if (Date.now() - entry.ts > RECO_CACHE_TTL) {
		_recoCacheMap.delete(key);
		return null;
	}
	return entry.html;
}

function saveRecoCache(key, html) {
	_recoCacheMap.set(key, { html, ts: Date.now() });
}

function showCrossSellFallback(grid) {
	const container = grid.closest('[data-smart-reco]');
	const fallbackTpl = container?.querySelector('[data-cross-sell-fallback]');
	if (fallbackTpl?.content) {
		const div = document.createElement('div');
		div.appendChild(fallbackTpl.content.cloneNode(true));
		const fallbackHTML = div.innerHTML.trim();
		if (fallbackHTML) {
			grid.innerHTML = fallbackHTML;
			if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
			return;
		}
	}
	grid.innerHTML = '';
}

async function mmLoadCartRecommendations() {
	const grid = document.querySelector('[data-smart-reco-grid]');
	if (!grid) return;

	// Locale prefix (e.g. "/de", "/fr") so API calls return translated content
	const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');

	// Le produit de référence et les IDs du panier viennent du Liquid (mis à jour à chaque refreshDOM)
	const refProductId = grid.dataset.refProduct;
	const cartProductIds = new Set((grid.dataset.cartProductIds || '').split(',').filter(Boolean));

	if (!refProductId) {
		showCrossSellFallback(grid);
		return;
	}

	// Invalide le cache si le produit de référence a changé
	if (mmLoadCartRecommendations._lastRef && mmLoadCartRecommendations._lastRef !== refProductId) {
		_recoCacheMap.clear();
	}
	mmLoadCartRecommendations._lastRef = refProductId;

	const cacheKey = `reco-${refProductId}`;
	const cached = getRecoCache(cacheKey);
	if (cached) {
		const g = document.querySelector('[data-smart-reco-grid]');
		if (g) { g.innerHTML = cached; if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms(); }
		return;
	}

	try {
		const recoData = await fetch(`${rootUrl}/recommendations/products.json?product_id=${refProductId}&intent=related&limit=6`)
			.then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); });

		const handles = recoData.products
			.filter(p => !cartProductIds.has(String(p.id)))
			.slice(0, 6)
			.map(p => p.handle);

		const gridFinal = document.querySelector('[data-smart-reco-grid]');
		if (!gridFinal) return;

		if (handles.length === 0) {
			showCrossSellFallback(gridFinal);
			return;
		}

		const htmlResponses = await Promise.all(
			handles.map(h => fetch(`${rootUrl}/products/${encodeURIComponent(h)}?view=cross-sell`).then(r => r.text()))
		);

		const g2 = document.querySelector('[data-smart-reco-grid]');
		if (!g2) return;

		let finalHTML = '';
		for (const html of htmlResponses) {
			const item = new DOMParser().parseFromString(html, 'text/html').querySelector('.mm-dc-cross-product');
			if (item) finalHTML += item.outerHTML;
		}

		if (!finalHTML) { showCrossSellFallback(g2); return; }

		g2.innerHTML = finalHTML;
		saveRecoCache(cacheKey, finalHTML);
		if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();

	} catch (error) {
		console.error('[cross-sell] error:', error.message);
		const g = document.querySelector('[data-smart-reco-grid]');
		if (g) showCrossSellFallback(g);
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	cartEmit('initializing');
	initCartQuantityListeners();
	initCrossSellSizeSelectors();
	initFreeSizeSelectors();
	initPromoCode();
	initDetailsAnimation();
	animateProgressBarFills();

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
			initCartEventListeners();
			window.initAddToCartForms();
		})
		.catch(error => {
			cartLog('error', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (CART_CONFIG.isDevelopment) {
	['opened', 'closed', 'quantity-increased', 'quantity-decreased', 'free-product-adding', 'count-updated', 'add-to-cart-start', 'add-to-cart-success', 'add-to-cart-error'].forEach(event => {
		document.addEventListener(`cart:${event}`, e => 
			console.log(`🛒 [CART] ${event}:`, e.detail)
		);
	});
}